import { __decorate, __metadata } from "tslib";
import { css, html } from 'lit';
import { customElement } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { when } from 'lit/directives/when.js';
import { globalStyles } from '../lit_css/global';
import { IconLitElement } from '../mixins/IconLitElement';
import { getCssProperty, maybeSetCssProperty } from '../utils';
export var CalloutType;
(function (CalloutType) {
    CalloutType["ERROR"] = "error";
    CalloutType["INFO"] = "info";
    CalloutType["SUCCESS"] = "success";
    CalloutType["WARNING"] = "warning";
})(CalloutType || (CalloutType = {}));
let C_Callout = class C_Callout extends IconLitElement {
    static get properties() {
        return {
            ...IconLitElement.properties,
            // internal state
            _hasIconSlot: { state: true },
            // props
            backgroundColor: { type: String, attribute: 'background-color' },
            boxShadow: { type: String, attribute: 'box-shadow' },
            clearable: { type: Boolean },
            color: { type: String },
            dense: { type: Boolean },
            fontSize: { type: String, attribute: 'font-size' },
            iconAlign: { type: String, attribute: 'icon-align' },
            iconGap: { type: String, attribute: 'icon-gap' },
            truncate: { type: Boolean },
            type: { type: String }
        };
    }
    // Lifecycle
    constructor() {
        super();
        this.useShadowDom = true;
        this.iconAlign = 'center';
        this.iconColor = 'currentColor';
    }
    updated(changedProperties) {
        super.updated(changedProperties);
        if (this.type === CalloutType.ERROR) {
            this.icon = this.icon || getCssProperty(this, '--c-default-callout-icon-error') || '';
            maybeSetCssProperty(this, '--c-default-callout-background-color', 'var(--c-default-error-color)');
            maybeSetCssProperty(this, '--c-default-callout-color', '#ffffff');
        }
        if (this.type === CalloutType.INFO) {
            this.icon = this.icon || getCssProperty(this, '--c-default-callout-icon-info') || '';
            maybeSetCssProperty(this, '--c-default-callout-background-color', 'var(--c-default-callout-info-color)');
            maybeSetCssProperty(this, '--c-default-callout-color', '#ffffff');
        }
        if (this.type === CalloutType.SUCCESS) {
            this.icon = this.icon || getCssProperty(this, '--c-default-callout-icon-success') || '';
            maybeSetCssProperty(this, '--c-default-callout-background-color', 'var(--c-default-callout-success-color)');
            maybeSetCssProperty(this, '--c-default-callout-color', '#1A351A');
        }
        if (this.type === CalloutType.WARNING) {
            this.icon = this.icon || getCssProperty(this, '--c-default-callout-icon-warning') || '';
            maybeSetCssProperty(this, '--c-default-callout-background-color', 'var(--c-default-warning-color)');
            maybeSetCssProperty(this, '--c-default-callout-color', 'var(--c-default-text-color)');
        }
        maybeSetCssProperty(this, '--c-default-icon-color', this.iconColor);
        maybeSetCssProperty(this, '--c-default-callout-icon-align', this.iconAlign);
        maybeSetCssProperty(this, '--c-default-callout-icon-gap', this.iconGap);
        maybeSetCssProperty(this, '--c-default-callout-background-color', this.backgroundColor);
        maybeSetCssProperty(this, '--c-default-callout-box-shadow', this.boxShadow);
        maybeSetCssProperty(this, '--c-default-callout-color', this.color);
        maybeSetCssProperty(this, '--c-default-callout-font-size', this.fontSize);
    }
    // Methods
    handleIconSlotChange(event) {
        const slot = event.target;
        this._hasIconSlot = slot.assignedElements().length > 0;
    }
    // Emitters
    emitClear() {
        this.dispatchEvent(new CustomEvent('c-clear'));
    }
    render() {
        const classes = {
            callout: true,
            dense: this.dense,
            'has-icon': this._hasIconSlot || Boolean(this.icon),
            'text-truncate': this.truncate
        };
        const partNames = Object.entries(classes)
            .filter(([_, value]) => value)
            .map(([key, _]) => key)
            .join(' ');
        return html `
      <div class=${classMap(classes)} part=${partNames}>
        <slot name="icon" @slotchange=${this.handleIconSlotChange}>
          ${when(this.icon, () => html `<c-icon-fa
                flex
                icon=${this.icon}
                icon-color=${this.iconColor}
                icon-size=${this.iconSize}
                use-shadow-dom
              ></c-icon-fa>`, () => '')}
        </slot>

        ${when(this._hasIconSlot || Boolean(this.icon), () => html ` <span class="text"><slot></slot></span> `, () => html `<slot></slot>`)}
        ${when(this.clearable, () => html `
              <c-button
                @c-click=${this.emitClear}
                color="rgba(220, 220, 220, 0.125)"
                class="clear-button"
                small
                text-color="currentColor"
                >Close</c-button
              >
            `)}
      </div>
    `;
    }
    static styles = css `
    ${globalStyles}

    :host {
      --c-default-callout-background-color: var(--c-default-text-color);
      --c-default-callout-border-radius: 4px;
      --c-default-callout-box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
      --c-default-callout-color: #fff;
      --c-default-callout-font-size: 18px;
      --c-default-callout-icon-align: flex-start;
      --c-default-callout-icon-gap: 0.5rem;

      /* overridable callout type icons */
      --c-default-callout-icon-info: 'fa-duotone fa-info-circle';
      --c-default-callout-icon-success: 'fa-duotone fa-solid fa-check-circle';
      --c-default-callout-icon-warning: 'fa-duotone fa-solid fa-exclamation-triangle';
      --c-default-callout-icon-error: 'fa-duotone fa-exclamation';

      --c-default-callout-info-color: #1976d2;
      --c-default-callout-success-color: #5bc35e;

      display: block;
      font-size: var(--c-para-font-size, var(--c-default-callout-font-size));
    }

    /* Assign overflow hidden to the host if using the truncate prop. */
    :host([truncate]) {
      overflow: hidden;
    }

    .callout {
      align-items: center;
      background-color: var(--c-callout-background-color, var(--c-default-callout-background-color));
      border-radius: var(--c-callout-border-radius, var(--c-default-callout-border-radius));
      box-shadow: var(--c-callout-box-shadow, var(--c-default-callout-box-shadow));
      color: var(--c-para-color, var(--c-default-callout-color));
      display: flex;
      font-size: var(--c-para-font-size, var(--c-default-callout-font-size));
      gap: var(--c-para-icon-gap, var(--c-default-callout-icon-gap));
      line-height: 1.3;
      margin: 0;
      padding: 0.75rem 1rem;

      &.dense {
        padding: 0.25rem 0.33rem;
      }

      &.has-icon {
        display: flex;
        align-items: var(--c-para-icon-align, var(--c-default-callout-icon-align));
      }
    }

    .clear-button {
      margin: -0.25rem;
      margin-left: auto;

      /* reduce padding on the button */
      &::part(button) {
        box-shadow: 0 2px 3px rgba(0, 0, 0, 0.1);
        height: auto;
        padding: 0.25rem 0.5rem;
      }
    }

    .text-truncate {
      overflow: hidden !important;
      text-overflow: ellipsis !important;
      line-height: 1.1 !important;
      white-space: nowrap !important;
    }
  `;
};
C_Callout = __decorate([
    customElement('c-callout'),
    __metadata("design:paramtypes", [])
], C_Callout);
export { C_Callout };
